import type {FC, MouseEvent} from 'react';
import {useCallback, useEffect, useMemo, useState,} from 'react';
import {Button, Modal, ModalBody, ModalFooter, ModalHeader,} from 'reactstrap';
import {useDispatch} from 'react-redux';
import {UrpsActions, useManagedUserContext, useUserRoleAssignments} from '@touchnet/user-management';
import type {RoleAssignmentResource} from '@tn-types/users-roles-permissions-storage';

import RoleAssignmentDisplay from './roles/role-assignment-display'
import UserSummaryBar from './user-summary-bar'
import useConfirmation from '~/hooks/use-confirmation';
import useEffectOnce from '~/hooks/use-effect-once';


export const UserDetailsModal: FC = () => {
    const {selectUser, urn} = useManagedUserContext();

    const closeModal = useCallback(() => {
        selectUser(null);
    }, [selectUser]);

    const dispatch = useDispatch();
    const roles = useUserRoleAssignments(urn);

    const [saving, setSaving] = useState<boolean>(false);
    const [whoaSlowDownThere, setSlowItDown] = useState<boolean>(false);
    const isRolesEmpty: boolean = useMemo(() => {
        return Object.keys(roles).length === 0
    }, [roles]);

    const confirmClose = useConfirmation(
        'You have unsaved changes. Are you sure you want to close the modal?',
        {success: closeModal}
    );

    const handleClose = useCallback(
        (e: MouseEvent) => {
            closeModal();
        },
        [
            closeModal,
            confirmClose,
        ]
    );

    const userRoles = useMemo<RoleAssignmentResource[]>(
        () => Object.values(roles),
        [roles]
    );


    useEffect(
        () => {
            if (!saving) {
                return;
            }
        },
        [
            saving,
            setSaving,
            whoaSlowDownThere,
        ]
    );

    useEffectOnce(() => {
        dispatch(UrpsActions.fetchUserRoleAssignments(urn));
    });

    return (
        <Modal isOpen scrollable toggle={closeModal} size="lg">
            <ModalHeader toggle={closeModal}>
                User Details
            </ModalHeader>
            <ModalBody>
                <UserSummaryBar
                    urn={urn}
                />
                {isRolesEmpty && <div>There are no role assignments for this user.</div>}
                {userRoles.map((assignment: RoleAssignmentResource) => (
                    <RoleAssignmentDisplay
                        key={assignment.roleCode}
                        assignment={assignment}
                        urn={urn}
                    />
                ))}
            </ModalBody>
            <ModalFooter>
                {
                    <Button color="primary" onClick={handleClose}>
                        Close
                    </Button>
                }
            </ModalFooter>
        </Modal>
    );
};

export default UserDetailsModal;
